import { useEffect, useRef, useState } from "react";
import { MessageCircle, Bot, X, Send } from "lucide-react";
import { API } from "../../lib/api";
import { useLang } from "../../lib/ctx";

const sid = () => {
  let s = localStorage.getItem("ddv_chat_sid");
  if (!s) {
    s = `chat_${Math.random().toString(36).slice(2)}${Date.now()}`;
    localStorage.setItem("ddv_chat_sid", s);
  }
  return s;
};

const linkify = (text) => text.split(/(https?:\/\/\S+)/g).map((p, i) => (p.startsWith("http") ? <a key={i} href={p} target="_blank" rel="noreferrer" className="text-[#C8A45D] underline">{p}</a> : p));

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    const m = text.trim();
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", text: m }, { role: "bot", text: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid(), message: m, lang }) });
      if (!res.ok) throw new Error("chat failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          try {
            const { t: chunk } = JSON.parse(d);
            setMsgs((x) => [...x.slice(0, -1), { role: "bot", text: x[x.length - 1].text + chunk }]);
          } catch (err) { /* partial */ }
        }
      }
    } catch (err) {
      setMsgs((x) => [...x.slice(0, -1), { role: "bot", text: "Sorry, please WhatsApp us at https://wa.me/60123655966" }]);
    }
    setBusy(false);
  };
  return (
    <>
      <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-3 no-print">
        <button onClick={() => setOpen(!open)} className="grid h-12 w-12 place-items-center border border-[#C8A45D] bg-black text-[#C8A45D] shadow-lg transition-transform duration-300 hover:scale-105" data-testid="chat-toggle-btn" aria-label="AI assistant"><Bot className="h-5 w-5" /></button>
        <a href="https://wa.me/60123655966" target="_blank" rel="noreferrer" className="grid h-12 w-12 place-items-center bg-[#25D366] text-white shadow-lg transition-transform duration-300 hover:scale-105" data-testid="whatsapp-float-btn" aria-label={t("whatsapp")}><MessageCircle className="h-6 w-6" /></a>
      </div>
      {open && (
        <div className="fixed bottom-24 right-5 z-50 flex h-[480px] max-h-[70vh] w-[calc(100vw-2.5rem)] max-w-sm flex-col border border-white/15 bg-[#0d0d0d] shadow-2xl rise" data-testid="chat-panel">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <p className="font-display text-xl tracking-widest">{t("chatTitle")}</p>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4 text-sm">
            <p className="bg-[#1B1B1B] p-3 text-zinc-300">{t("chatHello")}</p>
            {msgs.map((m, i) => (
              <p key={i} className={`whitespace-pre-wrap p-3 ${m.role === "user" ? "ml-8 bg-[#C8A45D] text-black" : "mr-8 bg-[#1B1B1B] text-zinc-200"}`} data-testid={`chat-msg-${m.role}`}>{m.text ? linkify(m.text) : "…"}</p>
            ))}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="flex border-t border-white/10">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("chatPlaceholder")} className="flex-1 bg-transparent px-3 py-3 text-sm outline-none" data-testid="chat-input" />
            <button disabled={busy} className="px-4 text-[#C8A45D] disabled:opacity-40" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
    </>
  );
};
